import React, { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './chmod-calculator.component.module.scss';

interface ChmodState {
    perms: boolean[][]; // [owner, group, others] x [r, w, x]
    numericInput: string;
    copied: boolean;
    error: string;
}

const ROLES = ['Owner', 'Group', 'Others'];
const BITS = ['Read', 'Write', 'Execute'];

function permsToOctal(perms: boolean[][]): string {
    return perms.map(role => (role[0] ? 4 : 0) + (role[1] ? 2 : 0) + (role[2] ? 1 : 0)).join('');
}

function octalToPerms(octal: string): boolean[][] {
    return octal.split('').map(d => {
        const n = parseInt(d, 10);
        return [(n & 4) !== 0, (n & 2) !== 0, (n & 1) !== 0];
    });
}

function permsToSymbolic(perms: boolean[][]): string {
    return perms.map(r => (r[0] ? 'r' : '-') + (r[1] ? 'w' : '-') + (r[2] ? 'x' : '-')).join('');
}

export default class ChmodCalculatorComponent extends Component<{ feature?: string }, ChmodState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;

    state: ChmodState = {
        perms: [[true, true, true], [true, false, true], [true, false, true]],
        numericInput: '755', copied: false, error: '',
    };

    togglePerm = (role: number, bit: number) => {
        const perms = this.state.perms.map(r => [...r]);
        perms[role][bit] = !perms[role][bit];
        this.setState({ perms, numericInput: permsToOctal(perms), error: '' });
    };

    handleNumeric = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        this.setState({ numericInput: val });
        if (/^[0-7]{3}$/.test(val)) {
            this.setState({ perms: octalToPerms(val), error: '' });
        } else if (val.length >= 3) {
            this.setState({ error: 'Enter a valid 3-digit octal (e.g. 755)' });
        }
    };

    copy = () => {
        const cmd = `chmod ${permsToOctal(this.state.perms)} filename`;
        navigator.clipboard.writeText(cmd).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { perms, numericInput, copied, error } = this.state;
        const octal = permsToOctal(perms);
        const symbolic = permsToSymbolic(perms);

        return (
            <>
                <div className="row">
                <div className="col-12">
                    <div className={styles.grid}>
                        <div />
                        {BITS.map(b => <div key={b} className={styles.permLabel}>{b}</div>)}
                        {ROLES.map((role, ri) => (
                            <React.Fragment key={role}>
                                <div className={styles.roleLabel}>{role}</div>
                                {[0, 1, 2].map(bi => (
                                    <div key={bi} className={styles.checkCell}>
                                        <input type="checkbox" checked={perms[ri][bi]}
                                            onChange={() => this.togglePerm(ri, bi)}
                                            aria-label={`${role} ${BITS[bi]}`} />
                                    </div>
                                ))}
                            </React.Fragment>
                        ))}
                    </div>

                    <div className={styles.numericRow}>
                        <label className={sharedStyles.label} style={{ margin: 0 }}>Numeric:</label>
                        <input className={styles.numericInput} value={numericInput} onChange={this.handleNumeric}
                            maxLength={3} aria-label="Octal permission" />
                        <span className={styles.symbolic}>{symbolic}</span>
                    </div>
                    {error && <div className={sharedStyles.errorMessage} aria-live="polite">⚠ {error}</div>}

                    <div className={styles.command}>
                        <span>chmod {octal} filename</span>
                        <button className={`${sharedStyles.copyButton} ${copied ? sharedStyles.copyButtonSuccess : ''}`}
                            onClick={this.copy}>{copied ? '✓ Copied' : 'Copy'}</button>
                    </div>
                </div>
            </div>
            </>
        );
    }
}
